import React from "react";
import { useForm } from "react-hook-form";
import { Button, Input } from "../../atoms";
import { FormField } from "../FormField";
import { FormHelpCallout } from "../FormHelpCallout";
import { calculateRevenueAtMonth } from "../../../utils/chart/scaling.utils";
import { safeDate } from "../../../utils/date";
import type { ReviseRevenueData } from "../../../services/api/financial.service";
import type { Revenue } from "../../../types/startup.types";

export interface RevenueRevisionFormProps {
  revenue: Revenue;
  defaultEffectiveDate?: string;
  onSubmit: (data: ReviseRevenueData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

interface RevenueRevisionFormValues {
  effectiveDate: string;
  monthlyAmount: number;
  growthRate: number;
  frequency: "monthly" | "quarterly" | "annual";
  revisionNote?: string;
}

function toRevenueForCalculation(revenue: Revenue) {
  return {
    ...revenue,
    amount: revenue.monthlyAmount ?? revenue.amount ?? 0,
    type: (revenue.category || revenue.type || "sales") as
      | "funding"
      | "sales"
      | "investment"
      | "grant"
      | "other",
    date: revenue.startDate,
    startupId: revenue.startupId,
    frequency:
      revenue.frequency === "one-time"
        ? ("monthly" as const)
        : revenue.frequency === "yearly"
          ? ("yearly" as const)
          : revenue.frequency || ("monthly" as const),
  };
}

export const RevenueRevisionForm: React.FC<RevenueRevisionFormProps> = ({
  revenue,
  defaultEffectiveDate,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const initialEffectiveDate =
    defaultEffectiveDate || new Date().toISOString().split("T")[0];

  const modeledAmount = React.useMemo(() => {
    const asOf = safeDate(initialEffectiveDate);
    return calculateRevenueAtMonth(toRevenueForCalculation(revenue), asOf);
  }, [revenue, initialEffectiveDate]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RevenueRevisionFormValues>({
    defaultValues: {
      effectiveDate: initialEffectiveDate,
      monthlyAmount: Math.round(modeledAmount * 100) / 100,
      growthRate: revenue.growthRate ? revenue.growthRate * 100 : 0,
      frequency:
        revenue.frequency === "yearly"
          ? "annual"
          : revenue.frequency === "quarterly"
            ? "quarterly"
            : "monthly",
      revisionNote: "",
    },
  });

  const effectiveDate = watch("effectiveDate");
  const modeledAsOfDate = React.useMemo(() => {
    const asOf = safeDate(effectiveDate);
    if (!asOf) return modeledAmount;
    return calculateRevenueAtMonth(toRevenueForCalculation(revenue), asOf);
  }, [revenue, effectiveDate, modeledAmount]);

  const handleFormSubmit = (data: RevenueRevisionFormValues) => {
    onSubmit({
      effectiveDate: data.effectiveDate,
      monthlyAmount: Number(data.monthlyAmount),
      growthRate: Number(data.growthRate) / 100,
      frequency: data.frequency,
      revisionNote: data.revisionNote?.trim() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <FormHelpCallout>
        Revise <strong>{revenue.name}</strong> from a date forward with a new
        amount and growth rate. Past months stay as originally modeled.
      </FormHelpCallout>

      <FormField label="Effective date" error={errors.effectiveDate?.message}>
        <Input
          type="date"
          {...register("effectiveDate", {
            required: "Effective date is required",
          })}
        />
      </FormField>

      <div className="rounded-[14px] border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/80">
        Modeled monthly amount as of {effectiveDate || initialEffectiveDate}:{" "}
        <span className="font-medium text-white">
          ${modeledAsOfDate.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
      </div>

      <FormField label="New monthly amount" error={errors.monthlyAmount?.message}>
        <Input
          type="number"
          step="0.01"
          {...register("monthlyAmount", {
            required: "Monthly amount is required",
            min: { value: 0, message: "Amount must be positive" },
            valueAsNumber: true,
          })}
        />
      </FormField>

      <FormField
        label="New monthly growth rate (%)"
        error={errors.growthRate?.message}
        helperText="Resets from the effective date — 0 for flat income."
      >
        <Input
          type="number"
          step="0.01"
          {...register("growthRate", { valueAsNumber: true })}
        />
      </FormField>

      <FormField label="Frequency" error={errors.frequency?.message}>
        <select {...register("frequency")} className="sh-select">
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="annual">Annual</option>
        </select>
      </FormField>

      <FormField label="Note (optional)" error={errors.revisionNote?.message}>
        <Input
          {...register("revisionNote")}
          placeholder="e.g. raise, new contract rate"
        />
      </FormField>

      <div className="flex gap-3 pt-2">
        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={isLoading}
          className="flex-1"
        >
          {isLoading ? "Saving revision..." : "Save revision"}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="md"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default RevenueRevisionForm;
